import { format, isValid, parse } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Check, ChevronLeft, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { IMaskInput } from "react-imask";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import type {
  AdvancedFilterDateRangeValue,
  AdvancedFilterDefinition,
  AdvancedFilterNumberRangeValue,
  AdvancedFilterOption,
  AdvancedFilterValue,
} from "./data-table-advanced-filter.types";

interface AdvancedFilterPickerProps {
  definitions: AdvancedFilterDefinition[];
  activeFilters: Record<string, AdvancedFilterValue>;
  loadedOptions: Record<string, AdvancedFilterOption[]>;
  onApply: (key: string, value: AdvancedFilterValue) => void;
}

type PickerStep = "list" | "value";

export function AdvancedFilterPicker({
  definitions,
  activeFilters,
  loadedOptions,
  onApply,
}: AdvancedFilterPickerProps) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<PickerStep>("list");
  const [selected, setSelected] = useState<AdvancedFilterDefinition | null>(
    null,
  );
  const [draft, setDraft] = useState<AdvancedFilterValue>("");

  const groups = useMemo(() => {
    const map = new Map<string, AdvancedFilterDefinition[]>();
    for (const definition of definitions) {
      const group = definition.config.group ?? "Geral";
      map.set(group, [...(map.get(group) ?? []), definition]);
    }
    return map;
  }, [definitions]);

  const reset = () => {
    setStep("list");
    setSelected(null);
    setDraft("");
    setOpen(false);
  };

  const handleSelect = (definition: AdvancedFilterDefinition) => {
    setSelected(definition);
    setDraft(
      activeFilters[definition.key] ??
        (definition.config.type === "date-range" ||
        definition.config.type === "number-range"
          ? {}
          : ""),
    );
    setStep("value");
  };

  const canApply =
    selected?.config.type === "date-range"
      ? Boolean(
          (draft as AdvancedFilterDateRangeValue).startDate ||
          (draft as AdvancedFilterDateRangeValue).endDate,
        )
      : selected?.config.type === "number-range"
        ? Boolean(
            (draft as AdvancedFilterNumberRangeValue).min ||
            (draft as AdvancedFilterNumberRangeValue).max,
          )
        : Boolean(draft);

  const handleApply = () => {
    if (!selected || !canApply) return;
    onApply(selected.key, draft);
    reset();
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => (next ? setOpen(true) : reset())}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-1.5 text-foreground"
        >
          <Plus className="h-3.5 w-3.5" />
          Filtro
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        {step === "list" ? (
          <Command>
            <CommandInput placeholder="Buscar coluna..." className="h-9" />
            <CommandList>
              <CommandEmpty>Nenhuma coluna encontrada.</CommandEmpty>
              {Array.from(groups.entries()).map(([group, items]) => (
                <CommandGroup key={group} heading={group}>
                  {items.map((definition) => (
                    <CommandItem
                      key={definition.key}
                      value={definition.label}
                      onSelect={() => handleSelect(definition)}
                    >
                      {activeFilters[definition.key] !== undefined && (
                        <span className="mr-2 h-1.5 w-1.5 rounded-full bg-primary" />
                      )}
                      {definition.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        ) : selected ? (
          <div className="space-y-3 p-3">
            <button
              type="button"
              onClick={() => setStep("list")}
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
              <ChevronLeft className="h-4 w-4" />
              {selected.label}
            </button>

            <AdvancedFilterValueInput
              definition={selected}
              value={draft}
              loadedOptions={loadedOptions}
              onChange={setDraft}
            />

            <Button
              type="button"
              size="sm"
              className="w-full bg-foreground text-background hover:bg-foreground/90"
              disabled={!canApply}
              onClick={handleApply}
            >
              Aplicar
            </Button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

function AdvancedFilterValueInput({
  definition,
  value,
  loadedOptions,
  onChange,
}: {
  definition: AdvancedFilterDefinition;
  value: AdvancedFilterValue;
  loadedOptions: Record<string, AdvancedFilterOption[]>;
  onChange: (value: AdvancedFilterValue) => void;
}) {
  if (
    definition.config.type === "select" ||
    definition.config.type === "boolean"
  ) {
    const options: AdvancedFilterOption[] =
      definition.config.type === "boolean"
        ? [
            { key: "1", value: "Sim" },
            { key: "0", value: "Não" },
          ]
        : (
            definition.config.options ??
            loadedOptions[definition.key] ??
            []
          ).filter((option) => !option.key.startsWith("group-header-"));

    return (
      <Command>
        <CommandInput placeholder="Buscar..." className="h-9" />
        <CommandList>
          <CommandEmpty>Nenhuma opção encontrada.</CommandEmpty>
          <CommandGroup>
            {options.map((option) => (
              <CommandItem
                key={option.key}
                value={option.value}
                onSelect={() => onChange(option.key)}
              >
                {option.value}
                <Check
                  className={cn(
                    "ml-auto h-4 w-4",
                    value === option.key ? "opacity-100" : "opacity-0",
                  )}
                />
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </Command>
    );
  }

  if (definition.config.type === "date-range") {
    const range = (value as AdvancedFilterDateRangeValue) ?? {};
    return (
      <div className="space-y-2">
        <AdvancedFilterDateInput
          label="De"
          date={range.startDate}
          onChange={(date) => onChange({ ...range, startDate: date })}
        />
        <AdvancedFilterDateInput
          label="Até"
          date={range.endDate}
          onChange={(date) => onChange({ ...range, endDate: date })}
        />
      </div>
    );
  }

  if (definition.config.type === "number-range") {
    const range = (value as AdvancedFilterNumberRangeValue) ?? {};
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <span className="w-8 text-xs text-muted-foreground">Mín</span>
          <Input
            type="number"
            value={range.min ?? ""}
            onChange={(event) =>
              onChange({ ...range, min: event.target.value })
            }
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="w-8 text-xs text-muted-foreground">Máx</span>
          <Input
            type="number"
            value={range.max ?? ""}
            onChange={(event) =>
              onChange({ ...range, max: event.target.value })
            }
          />
        </div>
      </div>
    );
  }

  return (
    <Input
      autoFocus
      placeholder={
        definition.config.placeholder ??
        `Buscar ${definition.label.toLowerCase()}...`
      }
      value={(value as string) ?? ""}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

function AdvancedFilterDateInput({
  label,
  date,
  onChange,
}: {
  label: string;
  date: Date | undefined;
  onChange: (date: Date | undefined) => void;
}) {
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [inputValue, setInputValue] = useState(
    date ? format(date, "dd/MM/yyyy") : "",
  );

  const handleInputChange = (raw: string) => {
    setInputValue(raw);
    if (raw.replace(/\D/g, "").length === 8) {
      const parsed = parse(raw, "dd/MM/yyyy", new Date());
      if (isValid(parsed)) onChange(parsed);
    } else if (raw === "") {
      onChange(undefined);
    }
  };

  return (
    <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
      <div className="flex items-center gap-2">
        <span className="w-8 text-xs text-muted-foreground">{label}</span>
        <PopoverTrigger asChild>
          <div className="flex-1 rounded-md border px-2 py-1.5">
            <IMaskInput
              mask="00/00/0000"
              value={inputValue}
              onAccept={(v: string) => handleInputChange(v)}
              placeholder="dd/mm/aaaa"
              className="w-full bg-transparent text-sm outline-hidden"
              onClick={() => setCalendarOpen(true)}
            />
          </div>
        </PopoverTrigger>
      </div>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          locale={ptBR}
          mode="single"
          selected={date}
          defaultMonth={date || new Date()}
          onSelect={(selectedDate) => {
            if (selectedDate) {
              onChange(selectedDate);
              setInputValue(format(selectedDate, "dd/MM/yyyy"));
              setCalendarOpen(false);
            }
          }}
          autoFocus
        />
      </PopoverContent>
    </Popover>
  );
}
